import React from 'react';
import BaseCard from '../../../components/base/BaseCard';
import MainMetric from '../../../components/metrics/MainMetric';
import EffectiveMetric from '../../../components/metrics/EffectiveMetric';
import DetailGroup from '../../../components/metrics/DetailGroup';
import PositionTypeIndicator from '../../../components/metrics/PositionTypeIndicator';
import CopyableValue from '../../../components/common/CopyableValue';
import InfoHint from '../../../components/common/InfoHint';
import FitText from '../../../components/common/FitText';
import MultiExitTable from '../../../components/metrics/MultiExitTable';
import BigNumber from 'bignumber.js';
import { getLiquidationBufferInfo, formatR } from '../../../utils/liquidationBuffer';
import { formatPriceLevel } from '../../../utils/pricePrecision';

const ForexMetricsCard = ({ metrics = {}, pairSpec, entryPrice, values = {}, isLoading = false, viewMode = 'full' }) => {
  const toBN = (v) => v && v !== '--' ? new BigNumber(v) : null;
  const safeMetrics = {
    ...metrics,
    positionSize: toBN(metrics.positionSize),
    quantity: toBN(metrics.quantity),
    lots: toBN(metrics.lots),
    leverage: toBN(metrics.leverage),
    margin: toBN(metrics.margin),
    effectiveTake: toBN(metrics.effectiveTake),
    nominalTake: toBN(metrics.nominalTake),
    effectiveStop: toBN(metrics.effectiveStop),
    nominalStop: toBN(metrics.nominalStop),
    effectiveRR: toBN(metrics.effectiveRR),
    friction: toBN(metrics.friction),
    nominalRR: toBN(metrics.nominalRR),
    optimizedRR: toBN(metrics.optimizedRR),
    priceLevels: metrics.priceLevels || {},
    tradingFees: metrics.tradingFees || {},
    totalFees: metrics.totalFees || {}
  };

  const formatValue = (value, options = {}) => {
    if (value === '--') return '--';
    const { isMoney = false, isQuantity = false, isLeverage = false, isLots = false } = options;
    if (!value) return '0';
    try {
      if (!(value instanceof BigNumber)) return '0';
      const formatOptions = {
        decimalSeparator: '.',
        groupSeparator: ',',
        groupSize: 3
      };
      if (isMoney) return `$${value.toFormat(2, formatOptions)}`;
      if (isLots) return value.toFormat(2, formatOptions);
      if (isQuantity) {
        return value.isInteger() ? value.toFormat(0, formatOptions) : value.toFormat(2, formatOptions);
      }
      if (isLeverage) return value.toFormat(0, formatOptions);
      return value.toFormat(2, formatOptions);
    } catch (error) {
      console.error('Error formatting value:', error);
      return '0';
    }
  };

  const formatPips = (value) => {
    if (!value || !(value instanceof BigNumber)) return '--';
    const formatOptions = { decimalSeparator: '.', groupSeparator: ',', groupSize: 3 };
    return value.toFormat(1, formatOptions);
  };

  const formatPipValue = (value) => {
    if (!value || !(value instanceof BigNumber)) return '--';
    const formatOptions = { decimalSeparator: '.', groupSeparator: ',', groupSize: 3 };
    if (value.lt(0.01)) return `$${value.toFormat(4, formatOptions)}`;
    return `$${value.toFormat(2, formatOptions)}`;
  };

  // Pip calculations
  const pipInfo = (() => {
    if (!pairSpec || !entryPrice || !(entryPrice instanceof BigNumber) ||
        !safeMetrics.quantity || !(safeMetrics.quantity instanceof BigNumber)) {
      return null;
    }

    const pipSize = new BigNumber(pairSpec.pipSize);
    const quantity = safeMetrics.quantity;
    const quoteCurrency = pairSpec.symbol.split('/')[1];

    // Pip value for current position (in USD)
    const pipValue = quoteCurrency === 'USD'
      ? quantity.times(pipSize)
      : quantity.times(pipSize).dividedBy(entryPrice);

    // Stop distance in pips
    const stopPrice = safeMetrics.priceLevels?.stopLoss;
    const stopPips = stopPrice && stopPrice !== '--'
      ? entryPrice.minus(new BigNumber(stopPrice)).abs().dividedBy(pipSize)
      : null;

    // Optimized TP distance in pips
    const tpPrice = safeMetrics.priceLevels?.optimizedTakeProfit;
    const tpPips = tpPrice && tpPrice !== '--'
      ? new BigNumber(tpPrice).minus(entryPrice).abs().dividedBy(pipSize)
      : null;

    // Break-even distance in pips
    const bePrice = safeMetrics.priceLevels?.breakEven;
    const bePips = bePrice && bePrice !== '--'
      ? new BigNumber(bePrice).minus(entryPrice).abs().dividedBy(pipSize)
      : null;

    // Spread in pips (from values prop)
    const spreadPips = values.spread instanceof BigNumber ? values.spread : null;

    return { pipValue, stopPips, tpPips, bePips, spreadPips };
  })();

  // Lot type label (micro/mini)
  const getLotTypeLabel = (lots) => {
    if (!lots || !(lots instanceof BigNumber) || lots.isNaN() || lots.isZero()) return '';
    const val = lots.toNumber();
    if (val >= 1) return '';
    const fmtOpts = { decimalSeparator: '.', groupSeparator: ',', groupSize: 3 };
    if (val >= 0.1) {
      const count = lots.times(10).decimalPlaces(1);
      return ` — ${count.toFormat(count.isInteger() ? 0 : 1, fmtOpts)} mini`;
    }
    const count = lots.times(100).decimalPlaces(1);
    return ` — ${count.toFormat(count.isInteger() ? 0 : 1, fmtOpts)} micro`;
  };

  const pricePrefix = pairSpec?.type === 'usd_quote' ? '$' : '';

  // Multiple take-profits (scale-out)
  const multiOn = !!(values.multiTpEnabled && safeMetrics.multiExit);
  const me = safeMetrics.multiExit || {};
  const tpCount = values.tpCount || (me.rows ? me.rows.length : '');
  const multiExitTable = multiOn ? <MultiExitTable data={safeMetrics.multiExit} mode={values.tpMode} pricePrefix={pricePrefix} decimals={pairSpec?.decimals} /> : null;

  const bufferInfo = getLiquidationBufferInfo({
    liquidationPrice: safeMetrics.priceLevels.liquidationPrice,
    stopLoss: safeMetrics.priceLevels.stopLoss,
    entryPrice,
    requestedR: values.safetyBufferR,
    maxLeverage: values.maxLeverage,
  });
  const bufferToneClass = bufferInfo?.tone === 'danger'
    ? 'text-rose-400/90'
    : bufferInfo?.tone === 'warn'
      ? 'text-amber-400/90'
      : 'text-slate-400/80';

  // --- Reusable blocks (identical in Full and Compact; only the arrangement changes) ---
  const positionSizeBlock = (
    <div>
      <div className="flex items-center mb-1">
        <div className="text-primary-orange uppercase tracking-wider text-sm font-medium">
          Position Size
        </div>
        {safeMetrics.positionType && (
          <PositionTypeIndicator type={safeMetrics.positionType} />
        )}
      </div>
      <div className={`text-slate-300/90 font-bold text-4xl mb-1 ${safeMetrics.positionSize === '--' ? 'font-mono' : ''}`}>
        <FitText align="left">
          <CopyableValue
            value={formatValue(safeMetrics.positionSize, { isMoney: true })}
            label="Position Size"
            preserveFormatting
            size="large"
            showTooltip={false}
          />
        </FitText>
      </div>
      <div className={`text-slate-500 text-sm tracking-wide ${safeMetrics.lots === '--' ? 'font-mono' : ''}`}>
        <FitText align="left">
          <CopyableValue
            value={`Lots: ${formatValue(safeMetrics.lots, { isLots: true })}${getLotTypeLabel(safeMetrics.lots)} (${formatValue(safeMetrics.quantity, { isQuantity: true })} units)`}
            label="Lots"
            preserveFormatting
            size="small"
            showTooltip={false}
          />
        </FitText>
      </div>
      {safeMetrics.quantity instanceof BigNumber && safeMetrics.quantity.gt(0) && safeMetrics.quantity.lt(1000) && (
        <div className="text-rose-400/80 text-xs mt-1.5 leading-relaxed">
          Position too small to trade. Min: 1,000 units (0.01 micro).
          {values.risk instanceof BigNumber && safeMetrics.quantity.gt(0) && (
            <span> Real risk at min lot: ${values.risk.times(1000).dividedBy(safeMetrics.quantity).toFormat(2, { decimalSeparator: '.', groupSeparator: ',', groupSize: 3 })}</span>
          )}
        </div>
      )}
    </div>
  );

  const leverageValue = `${formatValue(safeMetrics.leverage, { isLeverage: true })}x`;
  const leverageSub = `Margin: ${formatValue(safeMetrics.margin, { isMoney: true })}`;
  const leverageBlock = <MainMetric label="Leverage" value={leverageValue} subValue={leverageSub} />;
  const leverageBlockRight = <MainMetric label="Leverage" value={leverageValue} subValue={leverageSub} valueAlign="right" />;

  const netProfitMetric = (
    <EffectiveMetric
      label="Net Profit"
      value={formatValue(multiOn ? me.totalNetProfit : safeMetrics.effectiveTake, { isMoney: true })}
      subValue={multiOn ? `${tpCount} take-profits` : `Gross: ${formatValue(safeMetrics.nominalTake, { isMoney: true })}`}
      isPositive={true}
    />
  );
  const netLossMetric = (
    <EffectiveMetric
      label="Net Loss"
      value={formatValue(safeMetrics.effectiveStop, { isMoney: true })}
      subValue={`Gross: ${formatValue(safeMetrics.nominalStop, { isMoney: true })}`}
      isPositive={false}
    />
  );
  const avgExitStr = multiOn && me.avgExitPrice && me.avgExitPrice.isFinite && me.avgExitPrice.isFinite()
    ? `${pricePrefix}${formatPriceLevel(String(me.avgExitPrice), pairSpec?.decimals)}` : '--';
  const rookieMetric = (
    <EffectiveMetric
      label={multiOn ? "Avg Exit" : "Rookie R:R"}
      value={multiOn ? avgExitStr : `${formatValue(safeMetrics.effectiveRR)}x`}
      subValue={multiOn ? `${tpCount} exits` : `Rookie Tax: ${formatValue(safeMetrics.friction)}R`}
      isPositive={multiOn ? true : false}
    />
  );
  const grossMetric = (
    <EffectiveMetric
      label={multiOn ? "Blended R:R" : "Gross R:R"}
      value={`${formatValue(multiOn ? me.blendedNetRR : safeMetrics.optimizedRR)}x`}
      subValue={multiOn ? `Net · ${tpCount} exits` : `Net R:R: ${formatValue(safeMetrics.nominalRR)}x`}
      isPositive={true}
    />
  );

  // "Liq. buffer" row carries the (sometimes long) buffer explanation as a hover
  // info icon instead of a caption under the card.
  const liqBufferLabel = bufferInfo?.message ? (
    <span className="inline-flex items-center gap-1">
      Liq loss
      <InfoHint text={bufferInfo.message} className={bufferToneClass} />
    </span>
  ) : "Liq loss";

  const liquidationItem = {
    label: "Liquidation",
    value: `${pricePrefix}${formatPriceLevel(safeMetrics.priceLevels.liquidationPrice, pairSpec?.decimals)}`,
    className: "text-rose-400"
  };
  const bufferItem = bufferInfo ? {
    label: liqBufferLabel,
    value: `${formatR(bufferInfo.lossAtLiqR)}R`,
    className: bufferToneClass
  } : null;
  const breakEvenItem = { label: "Break-even", value: `${pricePrefix}${formatPriceLevel(safeMetrics.priceLevels.breakEven, pairSpec?.decimals)}` };
  const nominalTpItem = { label: "Nominal TP", value: `${pricePrefix}${formatPriceLevel(safeMetrics.priceLevels.takeProfit, pairSpec?.decimals)}` };
  const optimizedTpItem = { label: "Optimized TP", value: `${pricePrefix}${formatPriceLevel(safeMetrics.priceLevels.optimizedTakeProfit, pairSpec?.decimals)}` };

  // Multi-TP: targets already live in the exits table — keep Price Levels clean.
  const tpLevelItems = multiOn ? [] : [nominalTpItem, optimizedTpItem];
  const priceLevelsItems = [liquidationItem, ...(bufferItem ? [bufferItem] : []), breakEvenItem, ...tpLevelItems];
  const priceLevelsLeftItems = [liquidationItem, ...(bufferItem ? [bufferItem] : []), breakEvenItem];
  const priceLevelsRightItems = tpLevelItems;

  const priceLevelsGroup = <DetailGroup title="Price Levels" items={priceLevelsItems} />;

  // Compact: one "Price Levels" header, items split across two columns.
  const priceLevelsSplit = (
    <div>
      <div className="flex items-center gap-2 text-xs mb-1.5">
        <span className="text-slate-400 uppercase tracking-wider font-medium">Price Levels</span>
        <div className="h-px flex-1 bg-white/5" />
      </div>
      <div className="grid grid-cols-2 gap-x-6 sm:gap-x-10">
        <DetailGroup items={priceLevelsLeftItems} />
        <DetailGroup items={priceLevelsRightItems} />
      </div>
    </div>
  );

  const pipSizeBN = pairSpec ? new BigNumber(pairSpec.pipSize) : null;
  const tpPipItems = (multiOn && pipSizeBN && pipSizeBN.gt(0) && entryPrice instanceof BigNumber)
    ? me.rows.map((r, i) => ({ label: `TP${i + 1}`, value: `${formatPips(new BigNumber(r.targetPrice).minus(entryPrice).abs().dividedBy(pipSizeBN))} pips` }))
    : [{ label: "Target", value: pipInfo?.tpPips ? `${formatPips(pipInfo.tpPips)} pips` : '--' }];
  const pipInfoGroup = (
    <DetailGroup
      title="Pip Info"
      items={[
        { label: "Pip Value", value: pipInfo ? formatPipValue(pipInfo.pipValue) : '--' },
        { label: "Spread", value: pipInfo?.spreadPips ? `${formatPips(pipInfo.spreadPips)} pips` : '--' },
        { label: "Stop", value: pipInfo?.stopPips ? `${formatPips(pipInfo.stopPips)} pips` : '--' },
        ...tpPipItems,
        { label: "Break-Even", value: pipInfo?.bePips ? `${formatPips(pipInfo.bePips)} pips` : '--' },
      ]}
    />
  );

  const tradingFeesGroup = (
    <DetailGroup
      title="Trading Fees"
      items={[
        { label: "Entry Fee", value: formatValue(safeMetrics.tradingFees.entryFee, { isMoney: true }) },
        { label: "TP Fee", value: formatValue(safeMetrics.tradingFees.tpFee, { isMoney: true }) },
        { label: "SL Fee", value: formatValue(safeMetrics.tradingFees.slFee, { isMoney: true }) },
      ]}
    />
  );

  const totalFeesGroup = (
    <DetailGroup
      title="Total Fees"
      items={[
        { label: multiOn ? "All TPs" : "Take Profit", value: formatValue(multiOn ? me.totalFee : safeMetrics.totalFees.takeProfitFees, { isMoney: true }) },
        { label: "Stop Loss", value: formatValue(safeMetrics.totalFees.stopLossFees, { isMoney: true }) },
        ...(values.nights instanceof BigNumber && values.nights.gt(0) && safeMetrics.feeBreakdown?.swapCost
          ? [{ label: "Swap", value: safeMetrics.feeBreakdown.swapCost }]
          : []),
      ]}
    />
  );

  return (
    <BaseCard className={isLoading ? 'opacity-50 transition-opacity duration-200' : ''}>
      <div className="pt-6 px-4 pb-6 sm:pt-10 sm:px-8 sm:pb-10">
        {viewMode === 'compact' ? (
          /* ----- COMPACT: hero + 3 metrics (row) · Price Levels split below ----- */
          <div className="[&_.text-4xl]:text-3xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 lg:items-start">
              {/* LEFT: Position Size (left) + Leverage (right) — leverage never wraps */}
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">{positionSizeBlock}</div>
                <div className="shrink-0 w-[42%] max-w-[10rem]">{leverageBlockRight}</div>
              </div>
              {/* RIGHT: Gross R:R / Net Profit / Net Loss in a row */}
              <div className="grid grid-cols-3 gap-3 sm:gap-5">
                {grossMetric}
                {netProfitMetric}
                {netLossMetric}
              </div>
            </div>
            {multiExitTable && <div className="mt-5">{multiExitTable}</div>}
            {/* Price Levels — one header, two columns */}
            <div className="mt-5">
              {priceLevelsSplit}
            </div>
          </div>
        ) : (
          /* ----- FULL: today's layout, made responsive ----- */
          <>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              {/* Left column */}
              <div className="lg:col-span-5 space-y-8">
                {positionSizeBlock}
                {leverageBlock}
              </div>

              {/* Right column */}
              <div className="lg:col-span-7 grid grid-cols-2 gap-6">
                {netProfitMetric}
                {netLossMetric}
                {rookieMetric}
                {grossMetric}
              </div>
            </div>

            {multiExitTable && <div className="mt-8">{multiExitTable}</div>}

            {/* Bottom section */}
            <div className="pt-6 pb-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {priceLevelsGroup}
                {pipInfoGroup}
                {tradingFeesGroup}
                {totalFeesGroup}
              </div>
            </div>
          </>
        )}
      </div>
    </BaseCard>
  );
};

export default ForexMetricsCard;
